/**
 *  User experience & adoption
 *  Build quality & technical debt
 *  Automation, AI & efficiency
 *  Data & integrations
**/ const $42a804d610927c2a$export$aa473411bddb7e17 = [
    {
        group: "User experience & Adoption",
        shortLabel: "Company adoption",
        polarity: "good-practice",
        weight: 1,
        text: "What portion of your company uses Airtable in some capacity?",
        context: "Every additional team using Airtable increases the ROI of the platform and reduces fragmentation across the organisation.",
        answerAdvice: {
            "All": "<h4>Full adoption across every team.</h4><p>Airtable is your operational platform. Great work bringing everyone along.</p>",
            "Most": "<h4>Good progress towards adoption.</h4><p>Identify which teams aren't on Airtable yet and run a lightweight pilot. A workshop and a prototype can go a long way.</p>",
            "Some": "<h4>Room for growth.</h4><p>Identify 1-2 more teams whose workflows could move into Airtable and run a pilot. The payoff compounds as more teams join.</p>",
            "None": "<h4>No adoption yet.</h4><p>If no teams have adopted Airtable yet, find the use case with the strongest pull and start there. Wide adoption needs a visible internal success story first.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <!-- Spokes -->
  <line x1="140" y1="74" x2="44" y2="28" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="236" y2="28" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="44" y2="120" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="236" y2="120" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <!-- Central hub -->
  <rect x="106" y="40" width="68" height="68" rx="12" fill="#261299"/>
  <rect x="118" y="52" width="12" height="12" rx="3" fill="white" opacity="0.9"/>
  <rect x="134" y="52" width="12" height="12" rx="3" fill="white" opacity="0.6"/>
  <rect x="150" y="52" width="10" height="12" rx="3" fill="white" opacity="0.35"/>
  <rect x="118" y="68" width="12" height="10" rx="3" fill="white" opacity="0.6"/>
  <rect x="134" y="68" width="12" height="10" rx="3" fill="white" opacity="0.9"/>
  <rect x="118" y="82" width="12" height="10" rx="3" fill="white" opacity="0.35"/>
  <rect x="134" y="82" width="12" height="10" rx="3" fill="white" opacity="0.5"/>
  <!-- Team nodes: two person icons per corner -->
  <circle cx="32" cy="20" r="7" fill="white" stroke="#261299" stroke-width="1.5"/>
  <circle cx="46" cy="20" r="7" fill="white" stroke="#261299" stroke-width="1.5"/>
  <path d="M22 34 Q32 28 42 34" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <path d="M36 34 Q46 28 56 34" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <circle cx="224" cy="20" r="7" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <circle cx="238" cy="20" r="7" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <path d="M214 34 Q224 28 234 34" stroke="#6D1E6F" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <path d="M228 34 Q238 28 248 34" stroke="#6D1E6F" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <circle cx="32" cy="112" r="7" fill="white" stroke="#261299" stroke-width="1.5"/>
  <circle cx="46" cy="112" r="7" fill="white" stroke="#261299" stroke-width="1.5"/>
  <path d="M22 126 Q32 120 42 126" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <path d="M36 126 Q46 120 56 126" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <circle cx="224" cy="112" r="7" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <circle cx="238" cy="112" r="7" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <path d="M214 126 Q224 120 234 126" stroke="#6D1E6F" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <path d="M228 126 Q238 120 248 126" stroke="#6D1E6F" stroke-width="1.5" fill="none" stroke-linecap="round"/>
</svg>`
    },
    {
        group: "User experience & Adoption",
        shortLabel: "Interface-only access",
        polarity: "good-practice",
        weight: 2,
        text: "What portion of your end users are working in Interfaces?",
        context: "Interfaces create a true app layer with better workflows, enforced permissions and less noise.",
        answerAdvice: {
            "All": "<h4>You're deploying real software to your users.</h4><p>A proper app layer for your users provides a great foundation for data integrity and a professional user experience.</p>",
            "Most": "<h4>Good progress.</h4><p>Find the workflows still running in raw data and build interfaces for them. Users will grow to prefer a more focused user experience faster than you'd expect.</p>",
            "Some": "<h4>Room for growth.</h4><p>Moving users out of raw tables is the highest-leverage change you can make. Start with your most-used workflows and go from there. Users will grow to prefer a more focused experience quicker than you'd expect.</p>",
            "None": "<h4>No interfaces yet.</h4><p>Everyone started here in the data layer, and it's worth remembering that thousands of companies rolled out Airtable before interfaces existed. The key is to start small and lead by example. Interfaces are quick to spin up and very powerful once they're adopted as standard.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <!-- Left: raw data grid (bad) -->
  <rect x="16" y="34" width="90" height="72" rx="6" fill="white" stroke="#d0cdfc" stroke-width="1.5"/>
  <rect x="16" y="34" width="90" height="16" rx="6" fill="#f0effe"/>
  <line x1="46" y1="34" x2="46" y2="106" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="76" y1="34" x2="76" y2="106" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="16" y1="68" x2="106" y2="68" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="16" y1="88" x2="106" y2="88" stroke="#e8e6f8" stroke-width="1"/>
  <circle cx="61" cy="24" r="9" fill="#fee2e2"/>
  <path d="M57 20 L65 28 M65 20 L57 28" stroke="#d1242f" stroke-width="2" stroke-linecap="round"/>
  <!-- Arrow -->
  <path d="M112 70 L150 70" stroke="#261299" stroke-width="2.5" stroke-linecap="round"/>
  <path d="M145 65 L151 70 L145 75" fill="none" stroke="#261299" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
  <!-- Right: clean interface (good) -->
  <rect x="156" y="22" width="108" height="104" rx="8" fill="white" stroke="#261299" stroke-width="2"/>
  <rect x="156" y="22" width="108" height="24" rx="8" fill="#261299"/>
  <rect x="168" y="56" width="84" height="9" rx="4" fill="#eeedfc"/>
  <rect x="168" y="71" width="84" height="9" rx="4" fill="#eeedfc"/>
  <rect x="168" y="86" width="58" height="9" rx="4" fill="#eeedfc"/>
  <rect x="168" y="103" width="38" height="11" rx="5" fill="#261299"/>
  <circle cx="210" cy="12" r="9" fill="#dcfce7"/>
  <path d="M206 12 L209 15 L215 9" stroke="#0f8548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
    },
    {
        group: "User experience & Adoption",
        shortLabel: "User documentation",
        polarity: "good-practice",
        weight: 1,
        text: "What portion of your Airtable apps have supporting documentation?",
        context: "Every application has company-specific terminology, processes, automations hiding under the hood.",
        answerAdvice: {
            "All": "<h4>You're allowing your users to self-serve</h4><p>Accessible documentation prevents builders from being a single point of failure, and has probably saved you a lot of noise in your inbox.</p><p><b>Top tip:</b> Signpost your docs within interfaces to meet your users where they are.</p>",
            "Most": "<h4>You've made great progress in supporting your users.</h4><p>Documentation can feel endless. Try enlisting the help of power users to take ownership of some of the documentation.</p>",
            "Some": "<h4>You've kept a few support questions out of your inbox.</h4><p>Aim to get to 80% coverage of your key features. Try enlisting the help of power users to take ownership of some of the documentation.</p>",
            "None": "<h4>You haven't documented your apps yet.</h4><p>Start with one doc. A brief Loom walkthrough is a great start. Without it, you're a permanent dependency for anyone who needs help.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <rect x="116" y="34" width="76" height="96" rx="8" fill="#e0dff0"/>
  <rect x="108" y="26" width="76" height="96" rx="8" fill="white" stroke="#261299" stroke-width="2"/>
  <path d="M164 26 L164 56 L157 49 L150 56 L150 26 Z" fill="#6D1E6F"/>
  <rect x="120" y="42" width="20" height="7" rx="3" fill="#261299"/>
  <rect x="120" y="58" width="52" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="120" y="69" width="46" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="120" y="80" width="52" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="120" y="91" width="34" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="120" y="106" width="48" height="5" rx="2" fill="#d0cdfc"/>
</svg>`
    },
    {
        group: "Build quality & technical debt",
        shortLabel: "Naming hygiene",
        polarity: "good-practice",
        weight: 1,
        text: "Do your bases have descriptive names for tables, fields and automations?",
        context: "Descriptive names are a quick win, the cheapest sign that a system is maintainable.",
        answerAdvice: {
            "All": "<h4>You've completely removed guesswork for the next builder or AI reading your application.</h4><p>Clean naming makes everything easier to debug, hand over, and maintain.</p>",
            "Most": "<h4>Tidy up the stragglers.</h4><p>Generic names like 'Automation 3 copy' become landmines when something breaks unexpectedly.</p>",
            "Some": "<h4>Get a naming convention in place</h4><p>Set a naming convention now and apply it going forward.</p><p>Start with automations \u2014 they're the hardest to debug without clear names.</p>",
            "None": "<h4>Rename your tables, fields, and automations now.</h4><p>Descriptive names are the cheapest way to make a system maintainable, and the biggest nightmare when something breaks unexpectedly. You'll thank yourself later.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <rect x="40" y="14" width="200" height="120" rx="10" fill="white" stroke="#d0cdfc" stroke-width="1.5"/>
  <!-- Row 1 \u{2014} good (named table) -->
  <rect x="40" y="14" width="200" height="38" rx="10" fill="#f8f7ff"/>
  <circle cx="66" cy="33" r="10" fill="#dcfce7"/>
  <path d="M61 33 L64 36 L71 30" stroke="#0f8548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <rect x="82" y="26" width="80" height="7" rx="3" fill="#261299" opacity="0.8"/>
  <rect x="82" y="37" width="56" height="5" rx="2" fill="#d0cdfc"/>
  <!-- Row 2 \u{2014} good (named field) -->
  <circle cx="66" cy="71" r="10" fill="#dcfce7"/>
  <path d="M61 71 L64 74 L71 68" stroke="#0f8548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <rect x="82" y="64" width="96" height="7" rx="3" fill="#261299" opacity="0.8"/>
  <rect x="82" y="75" width="66" height="5" rx="2" fill="#d0cdfc"/>
  <!-- Row 3 \u{2014} bad (unnamed automation) -->
  <circle cx="66" cy="111" r="10" fill="#fee2e2"/>
  <path d="M61 107 L71 115 M71 107 L61 115" stroke="#d1242f" stroke-width="2" stroke-linecap="round"/>
  <rect x="82" y="104" width="116" height="7" rx="3" fill="#fecdca"/>
  <rect x="82" y="115" width="80" height="5" rx="2" fill="#fecdca" opacity="0.6"/>
</svg>`
    },
    {
        group: "Build quality & technical debt",
        shortLabel: "Sandbox usage",
        polarity: "good-practice",
        weight: 1.5,
        text: "Do you use Sandbox mode to test updates before publishing them?",
        context: "Sandbox mode lets you test changes on a copy of your base before pushing them live.",
        answerAdvice: {
            "Always": "<h4>Using the full toolkit to your advantage.</h4><p>Excellent release discipline. Sandbox use means production changes have been tested \u2014 far fewer surprises.</p>",
            "Mostly": "<h4>That's a solid habit for your dev process.</h4><p>Work out when you skip it and try to make Sandbox the default for everything beyond a trivial label change.</p>",
            "Sometimes": "<h4>Be careful experimenting in your production environment.</h4><p>Every live change is an experiment on real data. Get into the habit of using Sandbox for anything structural that could have adverse effects.</p>",
            "Never": "<h4>Try sandbox mode for your next structural update.</h4><p>One broken live automation costs more time than every sandbox test combined, and restoring from backup is not an easy process sadly.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <rect x="16" y="38" width="104" height="72" rx="10" fill="#faf9ff" stroke="#6D1E6F" stroke-width="2" stroke-dasharray="6 3"/>
  <path d="M126 74 L154 74" stroke="#261299" stroke-width="2.5" stroke-linecap="round"/>
  <path d="M149 69 L155 74 L149 79" fill="none" stroke="#261299" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
  <rect x="160" y="38" width="104" height="72" rx="10" fill="white" stroke="#261299" stroke-width="2"/>
</svg>`
    },
    {
        group: "Automation, AI & efficiency",
        shortLabel: "Automation run limits",
        polarity: "symptom",
        weight: 1.5,
        text: "Has your workspace run out of automation runs on more than one occasion?",
        context: "Every Airtable plan has a monthly cap on automation runs, be sure to plan ahead.",
        answerAdvice: {
            "Yes": "<h4>It happens to the best of us.</h4><p>Audit your automations for unnecessary triggers. Batching, conditional logic, and de-duplication can cut run usage dramatically. For workflows that don't need to run immediately, use a scheduled time (every x hours) to give yourself a predictable run count each month.</p>",
            "No": "<h4>That's impressive automation planning.</h4><p>Your automations are running within capacity/no wasted runs burning through your monthly limit.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <path d="M68 118 A 72 72 0 0 1 212 118" stroke="#e8e6f8" stroke-width="18" fill="none" stroke-linecap="round"/>
  <path d="M68 118 A 72 72 0 0 1 140 46" stroke="#0f8548" stroke-width="18" fill="none" stroke-linecap="round" opacity="0.65"/>
  <path d="M140 46 A 72 72 0 0 1 188 62" stroke="#c27803" stroke-width="18" fill="none" stroke-linecap="round" opacity="0.85"/>
  <path d="M188 62 A 72 72 0 0 1 212 118" stroke="#d1242f" stroke-width="18" fill="none" stroke-linecap="round"/>
  <line x1="140" y1="118" x2="198" y2="74" stroke="#261299" stroke-width="4" stroke-linecap="round"/>
  <circle cx="140" cy="118" r="8" fill="#261299"/>
  <circle cx="140" cy="118" r="3.5" fill="white"/>
</svg>`
    },
    {
        group: "Build quality & technical debt",
        shortLabel: "View/page cloning",
        polarity: "symptom-graded",
        weight: 1,
        text: "Do you have views or pages named for specific individuals?",
        context: "CRM (Jenny), CRM (Mike), CRM (David) etc. Cloning a view or page per person seems quick, but every design change is slower to roll out.",
        answerAdvice: {
            "Yes": "<h4>Replace per-person cloned views with filtered interface pages.</h4><p>Every clone adds maintenance debt when the design changes, and for every person who joins/leaves.</p>",
            "Some": "<h4>Make a plan to migrate those copies to filtered interface pages.</h4><p>When you catch yourself cloning, pause and ask if a filtered view or permission layer would handle it. It almost always does, and it scales with zero technical effort.</p>",
            "Never": "<h4>You've removed the need for copy/paste development.</h4><p>Filtered permission-based interfaces scale; per-person clones just don't.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <rect x="116" y="44" width="84" height="92" rx="6" fill="white" stroke="#e0dff0" stroke-width="1.5"/>
  <rect x="116" y="44" width="84" height="18" rx="6" fill="#f0effe"/>
  <rect x="126" y="70" width="54" height="5" rx="2" fill="#e8e6f8"/>
  <rect x="126" y="81" width="46" height="5" rx="2" fill="#e8e6f8"/>
  <rect x="102" y="32" width="84" height="92" rx="6" fill="white" stroke="#d0cdfc" stroke-width="1.5"/>
  <rect x="102" y="32" width="84" height="18" rx="6" fill="#eeedfc"/>
  <rect x="112" y="58" width="54" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="112" y="69" width="46" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="86" y="18" width="84" height="92" rx="6" fill="white" stroke="#261299" stroke-width="2"/>
  <rect x="86" y="18" width="84" height="20" rx="6" fill="#261299"/>
  <rect x="96" y="46" width="54" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="96" y="57" width="46" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="96" y="88" width="64" height="16" rx="8" fill="#eeedfc" stroke="#261299" stroke-width="1.5"/>
  <g transform="translate(5, 0)">
  <rect x="200" y="18" width="48" height="42" rx="8" fill="#fee2e2"/>
  <rect x="208" y="24" width="22" height="22" rx="4" fill="white" stroke="#d1242f" stroke-width="1.5"/>
  <rect x="216" y="32" width="22" height="22" rx="4" fill="white" stroke="#d1242f" stroke-width="1.5"/>
  </g>
</svg>`
    },
    {
        group: "Data & integrations",
        shortLabel: "Data syncing",
        polarity: "architecture",
        weight: 1.5,
        text: "Are you using synced data or published datasets to share data between bases?",
        context: "If the same master data (clients, products, team members) lives in multiple bases without syncing, it will gradually drift out of sync. Synced tables keep one source of truth.",
        answerAdvice: {
            "Yes": "<h4>You're managing a single source of truth.</h4><p>Synced tables keep one source of truth across your bases, that's the mature pattern for multi-base setups. Your organisation's already reaping the benefits.</p>",
            "No": "<h4>Start working on your master datasets.</h4><p>Set up synced tables or published datasets between your bases. Duplicated master data always drifts out of sync eventually. Sharing data means building new use cases on solid ground, with everyone working from the same information.</p>",
            "Not applicable": ""
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <rect x="16" y="34" width="96" height="80" rx="10" fill="white" stroke="#261299" stroke-width="2"/>
  <rect x="16" y="34" width="96" height="26" rx="10" fill="#261299"/>
  <rect x="28" y="70" width="64" height="7" rx="3" fill="#eeedfc"/>
  <rect x="28" y="83" width="54" height="7" rx="3" fill="#eeedfc"/>
  <rect x="28" y="96" width="60" height="7" rx="3" fill="#eeedfc"/>
  <rect x="168" y="34" width="96" height="80" rx="10" fill="white" stroke="#6D1E6F" stroke-width="2"/>
  <rect x="168" y="34" width="96" height="26" rx="10" fill="#6D1E6F"/>
  <rect x="180" y="70" width="64" height="7" rx="3" fill="#f5eef5"/>
  <rect x="180" y="83" width="54" height="7" rx="3" fill="#f5eef5"/>
  <rect x="180" y="96" width="60" height="7" rx="3" fill="#f5eef5"/>
  <path d="M116 64 Q140 58 164 64" stroke="#261299" stroke-width="2.5" fill="none" stroke-linecap="round"/>
  <path d="M159 59 L165 64 L159 69" fill="none" stroke="#261299" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M164 98 Q140 104 116 98" stroke="#6D1E6F" stroke-width="2.5" fill="none" stroke-linecap="round"/>
  <path d="M121 93 L115 98 L121 103" fill="none" stroke="#6D1E6F" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
    },
    {
        group: "Data & integrations",
        shortLabel: "External data sync",
        polarity: "architecture",
        weight: 1,
        text: "Do you have any integrations to sync data in/out of Airtable?",
        context: "Airtable becomes most valuable when it talks to the tools around it \u2014 CRM, email, calendar, accounting. Without integrations, data lives in silos and must be re-entered by hand.",
        answerAdvice: {
            "Yes": "<h4>You're connecting islands into a unified system.</h4><p>Airtable connected to your surrounding tools is Airtable as a system of record, instead of another isolated island.</p>",
            "No": "<h4>Start working on your integration strategy.</h4><p>Connect Airtable to your surrounding tools. Even a simple Zapier sync eliminates manual re-keying and starts making Airtable the hub.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <line x1="140" y1="74" x2="56" y2="28" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="224" y2="28" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="56" y2="120" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <line x1="140" y1="74" x2="224" y2="120" stroke="#d0cdfc" stroke-width="1.5" stroke-dasharray="4 2"/>
  <rect x="108" y="42" width="64" height="64" rx="12" fill="#261299"/>
  <rect x="120" y="54" width="12" height="12" rx="3" fill="white" opacity="0.9"/>
  <rect x="136" y="54" width="12" height="12" rx="3" fill="white" opacity="0.6"/>
  <rect x="152" y="54" width="8" height="12" rx="3" fill="white" opacity="0.35"/>
  <rect x="120" y="70" width="12" height="10" rx="3" fill="white" opacity="0.6"/>
  <rect x="136" y="70" width="12" height="10" rx="3" fill="white" opacity="0.9"/>
  <rect x="152" y="70" width="8" height="10" rx="3" fill="white" opacity="0.35"/>
  <rect x="120" y="84" width="12" height="8" rx="3" fill="white" opacity="0.35"/>
  <rect x="136" y="84" width="12" height="8" rx="3" fill="white" opacity="0.5"/>
  <!-- Top-left: email -->
  <rect x="28" y="10" width="40" height="30" rx="6" fill="white" stroke="#261299" stroke-width="1.5"/>
  <path d="M28 16 L48 27 L68 16" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <!-- Top-right: calendar -->
  <rect x="212" y="10" width="40" height="30" rx="6" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <rect x="218" y="16" width="28" height="18" rx="3" fill="#f0effe"/>
  <rect x="218" y="16" width="28" height="8" rx="3" fill="#6D1E6F"/>
  <circle cx="226" cy="31" r="2" fill="#6D1E6F" opacity="0.5"/>
  <circle cx="233" cy="31" r="2" fill="#6D1E6F" opacity="0.5"/>
  <!-- Bottom-left: people -->
  <rect x="28" y="108" width="40" height="30" rx="6" fill="white" stroke="#261299" stroke-width="1.5"/>
  <circle cx="48" cy="119" r="7" fill="#eeedfc" stroke="#261299" stroke-width="1"/>
  <path d="M36 136 Q48 130 60 136" stroke="#261299" stroke-width="1.5" fill="none" stroke-linecap="round"/>
  <!-- Bottom-right: lightning/zap -->
  <rect x="212" y="108" width="40" height="30" rx="6" fill="white" stroke="#6D1E6F" stroke-width="1.5"/>
  <path d="M235 114 L227 126 L233 126 L224 138 L240 124 L234 124 Z" fill="#6D1E6F"/>
</svg>`
    },
    {
        group: "Automation, AI & efficiency",
        shortLabel: "Automation coverage",
        polarity: "good-practice",
        weight: 1.5,
        text: "What portion of your teams have successfully automated at least one manual task?",
        context: "Automations are the difference between a spreadsheet and a system. If most workflows still require manual steps, you're leaving efficiency on the table.",
        answerAdvice: {
            "All": "<h4>You're fully automated.</h4><p>You're getting the most out of Airtable as an operational system, not just a database.</p>",
            "Most": "<h4>You're shedding repetitive tasks.</h4><p>Map the remaining manual processes and add automations. The effort per automation drops once you're in the habit.</p>",
            "Some": "<h4>Start automating your repetitive tasks.</h4><p>Map your repetitive workflows and add automations. Even a simple trigger-action rule eliminates manual steps and frees up your team.</p>",
            "None": "<h4>Begin your automation journey.</h4><p>Automations are what turn a spreadsheet into a system. Start with your most repetitive workflow \u2014 notify, create, update.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <!-- Step 1: Record -->
  <rect x="10" y="44" width="52" height="60" rx="8" fill="white" stroke="#261299" stroke-width="2"/>
  <rect x="10" y="44" width="52" height="18" rx="8" fill="#261299"/>
  <rect x="18" y="70" width="36" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="18" y="81" width="28" height="5" rx="2" fill="#d0cdfc"/>
  <rect x="18" y="92" width="32" height="5" rx="2" fill="#d0cdfc"/>
  <!-- Arrow 1 -->
  <path d="M66 74 L80 74" stroke="#261299" stroke-width="2" stroke-linecap="round"/>
  <path d="M77 71 L81 74 L77 77" fill="none" stroke="#261299" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <!-- Step 2: Trigger (lightning in circle) -->
  <circle cx="101" cy="74" r="22" fill="#261299"/>
  <path d="M105 58 L96 76 L103 76 L97 90 L114 72 L107 72 Z" fill="white" opacity="0.9"/>
  <!-- Arrow 2 -->
  <path d="M125 74 L139 74" stroke="#261299" stroke-width="2" stroke-linecap="round"/>
  <path d="M136 71 L140 74 L136 77" fill="none" stroke="#261299" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <!-- Step 3: Action (envelope) -->
  <rect x="142" y="50" width="52" height="48" rx="8" fill="white" stroke="#6D1E6F" stroke-width="2"/>
  <rect x="142" y="50" width="52" height="18" rx="8" fill="#6D1E6F" opacity="0.85"/>
  <rect x="150" y="78" width="36" height="5" rx="2" fill="#f5eef5"/>
  <rect x="150" y="88" width="24" height="5" rx="2" fill="#f5eef5"/>
  <!-- Arrow 3 -->
  <path d="M197 74 L211 74" stroke="#261299" stroke-width="2" stroke-linecap="round"/>
  <path d="M208 71 L212 74 L208 77" fill="none" stroke="#261299" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <!-- Step 4: Done checkmark -->
  <circle cx="232" cy="74" r="22" fill="#dcfce7" stroke="#0f8548" stroke-width="2"/>
  <path d="M222 74 L229 81 L243 67" stroke="#0f8548" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
    },
    {
        group: "Automation, AI & efficiency",
        shortLabel: "AI usage",
        polarity: "good-practice",
        weight: 0.5,
        text: "How much does your team use AI within Airtable?",
        context: "Airtable's AI features \u2014 AI fields, AI in automations can summarise records, classify data and attachments and draft communications automatically.",
        answerAdvice: {
            "A lot": "<h4>You're ahead of the curve.</h4><p>AI within Airtable compounds your efficiency gains. Users will be looking for new opportunities to leverage AI.</p>",
            "Sometimes": "<h4>Keep experimenting.</h4><p>AI fields for summarisation and classification are where the consistent time savings are.</p>",
            "A little": "<h4>Explore AI fields.</h4><p>Summarising or tagging records \u2014 they're often a quick drop-in that saves real time at scale.</p>",
            "Never": "<h4>Try an AI field.</h4><p>Start with one table \u2014 summarising notes or classifying records are low-effort, high-value starting points.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <!-- Record card -->
  <rect x="20" y="18" width="240" height="112" rx="10" fill="white" stroke="#261299" stroke-width="2"/>
  <rect x="20" y="18" width="240" height="26" rx="10" fill="#261299"/>
  <!-- Header label bar -->
  <rect x="32" y="26" width="72" height="8" rx="4" fill="white" opacity="0.35"/>
  <!-- AI badge in header -->
  <rect x="194" y="24" width="54" height="16" rx="8" fill="#6D1E6F"/>
  <rect x="202" y="29" width="38" height="6" rx="3" fill="white" opacity="0.7"/>
  <!-- Normal field rows -->
  <rect x="32" y="52" width="56" height="6" rx="3" fill="#d0cdfc"/>
  <rect x="32" y="64" width="196" height="8" rx="3" fill="#261299" opacity="0.12"/>
  <rect x="32" y="78" width="56" height="6" rx="3" fill="#d0cdfc"/>
  <!-- AI-generated field (dashed border, tinted) -->
  <rect x="32" y="90" width="196" height="30" rx="6" fill="#f0effe" stroke="#6D1E6F" stroke-width="1.5" stroke-dasharray="5 2.5"/>
  <rect x="40" y="98" width="110" height="6" rx="3" fill="#6D1E6F" opacity="0.45"/>
  <rect x="40" y="108" width="78" height="6" rx="3" fill="#6D1E6F" opacity="0.28"/>
  <!-- Sparkles -->
  <path d="M242 86 L244 80 L246 86 L252 88 L246 90 L244 96 L242 90 L236 88 Z" fill="#6D1E6F"/>
  <path d="M256 98 L257.5 93 L259 98 L264 99.5 L259 101 L257.5 106 L256 101 L251 99.5 Z" fill="#6D1E6F" opacity="0.55"/>
  <path d="M248 70 L249 67 L250 70 L253 71 L250 72 L249 75 L248 72 L245 71 Z" fill="#261299" opacity="0.45"/>
</svg>`
    },
    {
        group: "Build quality & technical debt",
        shortLabel: "Repeated columns",
        polarity: "symptom",
        weight: 1,
        text: "Do you have any repeated columns in tables?",
        context: "Month 1, Month 2, Month 3 or Person 1, Person 2, Person 3. Repeated columns come from treating a database like a spreadsheet.",
        answerAdvice: {
            "Yes": "<h4>Convert those repeated column groups.</h4><p>Convert those repeated column groups into a child table with one row per period/person, linked back to the parent. It enables proper filtering, rollups, and future growth without breaking the schema. See: 'database normalisation'.</p>",
            "No": "<h4>Great schema discipline.</h4><p>Avoiding repeated columns shows youre understanding how to shape relational data.</p>"
        },
        image: `<svg viewBox="0 0 280 148" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect width="280" height="148" rx="12" fill="#f0effe"/>
  <!-- Table container -->
  <rect x="14" y="22" width="252" height="104" rx="8" fill="white" stroke="#d0cdfc" stroke-width="1.5"/>
  <!-- Header row (repeated columns highlighted bad) -->
  <rect x="14" y="22" width="252" height="26" rx="8" fill="#fee2e2"/>
  <!-- Name column header -->
  <rect x="22" y="29" width="36" height="10" rx="3" fill="#fecdca" opacity="0.8"/>
  <!-- Month columns (repeated, highlighted red) -->
  <rect x="70" y="29" width="36" height="10" rx="3" fill="#d1242f" opacity="0.55"/>
  <rect x="116" y="29" width="36" height="10" rx="3" fill="#d1242f" opacity="0.55"/>
  <rect x="162" y="29" width="36" height="10" rx="3" fill="#d1242f" opacity="0.55"/>
  <!-- Last column fading with "..." -->
  <rect x="208" y="29" width="28" height="10" rx="3" fill="#d1242f" opacity="0.25"/>
  <!-- Column dividers -->
  <line x1="62" y1="22" x2="62" y2="126" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="108" y1="22" x2="108" y2="126" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="154" y1="22" x2="154" y2="126" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="200" y1="22" x2="200" y2="126" stroke="#e8e6f8" stroke-width="1"/>
  <!-- Row dividers -->
  <line x1="14" y1="48" x2="266" y2="48" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="14" y1="66" x2="266" y2="66" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="14" y1="84" x2="266" y2="84" stroke="#e8e6f8" stroke-width="1"/>
  <line x1="14" y1="102" x2="266" y2="102" stroke="#e8e6f8" stroke-width="1"/>
  <!-- Data cells -->
  <rect x="22" y="54" width="28" height="6" rx="3" fill="#d0cdfc"/>
  <rect x="70" y="54" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="116" y="54" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="162" y="54" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="208" y="54" width="20" height="6" rx="3" fill="#eeedfc"/>
  <rect x="22" y="72" width="32" height="6" rx="3" fill="#d0cdfc"/>
  <rect x="70" y="72" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="116" y="72" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="162" y="72" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="208" y="72" width="20" height="6" rx="3" fill="#eeedfc"/>
  <rect x="22" y="90" width="24" height="6" rx="3" fill="#d0cdfc"/>
  <rect x="70" y="90" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="116" y="90" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="162" y="90" width="24" height="6" rx="3" fill="#eeedfc"/>
  <rect x="208" y="90" width="20" height="6" rx="3" fill="#eeedfc"/>
  <rect x="22" y="108" width="30" height="6" rx="3" fill="#d0cdfc"/>
  <rect x="70" y="108" width="24" height="6" rx="3" fill="#eeedfc"/>
  <!-- Warning badge -->
  <g transform="translate(-20, -10)">
  <circle cx="270" cy="13" r="9" fill="#fee2e2"/>
  <path d="M266 9 L274 17 M274 9 L266 17" stroke="#d1242f" stroke-width="2" stroke-linecap="round"/>
  </g>
</svg>`
    }
];



const $4607f403a8f49bdc$var$SCORE = {
    min: 0,
    max: 10
};
const $4607f403a8f49bdc$var$NA_VALUES = new Set([
    "N/A",
    "Not applicable"
]);
function $4607f403a8f49bdc$export$46569753fba557ac(question, answerValue) {
    if ($4607f403a8f49bdc$var$NA_VALUES.has(answerValue)) return null;
    const validOptions = Object.keys(question.answerAdvice).filter((o)=>!$4607f403a8f49bdc$var$NA_VALUES.has(o));
    const validIdx = validOptions.indexOf(answerValue);
    if (validIdx === -1) return null;
    if (question.polarity === "good-practice") return 1 - validIdx / (validOptions.length - 1);
    if (question.polarity === "symptom") return answerValue === "Yes" ? 0 : 1;
    if (question.polarity === "symptom-graded") return validIdx / (validOptions.length - 1);
    if (question.polarity === "architecture") return answerValue === "Yes" ? 1 : 0;
    return 0;
}
function $4607f403a8f49bdc$export$e09e94e4e42c547d(answers) {
    let weightedSum = 0;
    let totalWeight = 0;
    const perQuestion = [];
    (0, $42a804d610927c2a$export$aa473411bddb7e17).forEach((q, idx)=>{
        const answer = answers[idx];
        const norm = $4607f403a8f49bdc$export$46569753fba557ac(q, answer);
        perQuestion.push({
            idx: idx,
            shortLabel: q.shortLabel,
            polarity: q.polarity,
            answer: answer,
            normScore: norm,
            weight: q.weight
        });
        if (norm !== null) {
            weightedSum += norm * q.weight;
            totalWeight += q.weight;
        }
    });
    const raw = totalWeight > 0 ? weightedSum / totalWeight : 0;
    const score = Math.round(raw * ($4607f403a8f49bdc$var$SCORE.max - $4607f403a8f49bdc$var$SCORE.min) * 10 + $4607f403a8f49bdc$var$SCORE.min * 10) / 10;
    return {
        score: score,
        perQuestion: perQuestion
    };
}
const $4607f403a8f49bdc$export$ded5a3e043e94a40 = (()=>{
    const map = new Map();
    (0, $42a804d610927c2a$export$aa473411bddb7e17).forEach((q, idx)=>{
        if (!q.group) return;
        if (!map.has(q.group)) map.set(q.group, []);
        map.get(q.group).push(idx);
    });
    return [
        ...map.entries()
    ].map(([label, questionIndices])=>({
            label: label,
            questionIndices: questionIndices
        }));
})();
function $4607f403a8f49bdc$export$b742977fb0973920(perQuestion) {
    const byIdx = Object.fromEntries(perQuestion.map((q)=>[
            q.idx,
            q
        ]));
    return $4607f403a8f49bdc$export$ded5a3e043e94a40.map(({ label: label, questionIndices: questionIndices })=>{
        let weightedSum = 0;
        let totalWeight = 0;
        questionIndices.forEach((idx)=>{
            const q = byIdx[idx];
            if (q && q.normScore !== null) {
                weightedSum += q.normScore * q.weight;
                totalWeight += q.weight;
            }
        });
        const norm = totalWeight > 0 ? weightedSum / totalWeight : null;
        const score = norm !== null ? Math.round(norm * 10) : null;
        return {
            label: label,
            score: score,
            norm: norm
        };
    });
}
function $4607f403a8f49bdc$export$bdf6804ae02ab389(perQuestion) {
    return perQuestion.filter((q)=>q.normScore !== null && q.normScore < 0.5).sort((a, b)=>a.normScore - b.normScore || b.weight - a.weight).slice(0, 2).map((q)=>({
            idx: q.idx,
            shortLabel: q.shortLabel,
            answer: q.answer
        }));
}



const $4ed1e0f3eb2386ad$var$STATUS = {
    good: {
        icon: "\u2705",
        label: "Strong"
    },
    warn: {
        icon: "\u26A0\uFE0F",
        label: "Needs attention"
    },
    bad: {
        icon: "\uD83D\uDD34",
        label: "At risk"
    }
};
function $4ed1e0f3eb2386ad$export$ac06e2e0fe5e2dd8(norm) {
    if (norm === null) return null;
    if (norm >= 0.75) return $4ed1e0f3eb2386ad$var$STATUS.good;
    if (norm >= 0.4) return $4ed1e0f3eb2386ad$var$STATUS.warn;
    return $4ed1e0f3eb2386ad$var$STATUS.bad;
}
function $4ed1e0f3eb2386ad$export$5752143a981bab69(perQuestion) {
    return perQuestion.map((q)=>({
            shortLabel: q.shortLabel,
            answer: q.answer,
            status: $4ed1e0f3eb2386ad$export$ac06e2e0fe5e2dd8(q.normScore),
            normScore: q.normScore
        }));
}
function $4ed1e0f3eb2386ad$export$2934054be1c59f77(score) {
    if (score >= 10) return {
        band: "Perfect",
        summary: "You've mastered how to deploy low-code operational software with Airtable."
    };
    if (score >= 8) return {
        band: "High",
        summary: "Your Airtable setup is well-architected. A few tweaks could take it further."
    };
    if (score >= 5) return {
        band: "Medium",
        summary: "You're running on a solid foundation, focus on the areas holding you back."
    };
    return {
        band: "Low",
        summary: "Your Airtable setup has gaps that will slow you down as usage grows."
    };
}



const $1906e9dcde8b5031$var$RESULTS_FUNCTION = "/.netlify/functions/submitQuizResults";
async function $1906e9dcde8b5031$export$972dc84f7790a83a({ email: email, uid: uid, score: score, topTwo: topTwo, source: source, groupScores: groupScores, perQuestion: perQuestion }) {
    const questionsAndAnswers = perQuestion.map((pq)=>{
        const q = (0, $42a804d610927c2a$export$aa473411bddb7e17)[pq.idx];
        return {
            questionIndex: pq.idx,
            questionText: q.text,
            answer: pq.answer,
            group: q.group
        };
    });
    const areaScores = groupScores.map((g)=>{
        let colour = "empty";
        if (g.norm !== null) {
            if (g.norm >= 0.75) colour = "green";
            else if (g.norm >= 0.4) colour = "orange";
            else colour = "red";
        }
        return {
            area: g.label,
            score: g.score,
            colour: colour
        };
    });
    const payload = {
        email: email || "",
        uid: uid || "",
        finalScore: score,
        topTwo: topTwo.map((t)=>t.shortLabel).join(", "),
        source: source || "organic",
        questionsAndAnswers: questionsAndAnswers,
        areaScores: areaScores
    };
    const resp = await fetch($1906e9dcde8b5031$var$RESULTS_FUNCTION, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
    });
    if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Submit failed (${resp.status}): ${text}`);
    }
    return resp.json();
}


const $1358149ea3f19aaa$var$SCREENS = [
    "hero",
    "quiz",
    "email",
    "results",
    "calendly",
    "confirmation"
];
// Questions reordered into category groups for sequential display
const $1358149ea3f19aaa$var$GROUPED_QUESTIONS = (0, $4607f403a8f49bdc$export$ded5a3e043e94a40).flatMap(({ questionIndices: questionIndices })=>questionIndices.map((idx)=>({
            idx: idx,
            question: (0, $42a804d610927c2a$export$aa473411bddb7e17)[idx]
        })));
// Category heading + position-within-category for each display index
const $1358149ea3f19aaa$var$Q_META = (0, $4607f403a8f49bdc$export$ded5a3e043e94a40).flatMap(({ label: label, questionIndices: questionIndices })=>questionIndices.map((_, i)=>({
            category: label,
            pos: i + 1,
            total: questionIndices.length
        })));
const $1358149ea3f19aaa$var$state = {
    currentScreen: "quiz",
    currentQuestion: 0,
    answers: {},
    email: "",
    name: "",
    score: null,
    topTwo: [],
    perQuestion: [],
    feedback: [],
    groupScores: [],
    source: "organic"
};
let $1358149ea3f19aaa$var$root;
function $1358149ea3f19aaa$var$$(sel, ctx) {
    return (ctx || $1358149ea3f19aaa$var$root).querySelector(sel);
}
function $1358149ea3f19aaa$var$$$(sel, ctx) {
    return (ctx || $1358149ea3f19aaa$var$root).querySelectorAll(sel);
}
const $1358149ea3f19aaa$var$params = new URLSearchParams(window.location.search);
const $1358149ea3f19aaa$var$DEV_MODE = $1358149ea3f19aaa$var$params.has("dev");
const $1358149ea3f19aaa$var$PREFILLED_UID = $1358149ea3f19aaa$var$params.get("qzc") || $1358149ea3f19aaa$var$params.get("uid");
function $1358149ea3f19aaa$var$init() {
    $1358149ea3f19aaa$var$root = document.querySelector(".airtable-maturity-assessment");
    if (!$1358149ea3f19aaa$var$root) return;
    if ($1358149ea3f19aaa$var$params.get("src") === "email") $1358149ea3f19aaa$var$state.source = "email";
    const progress = $1358149ea3f19aaa$var$$(".aq-progress");
    if (progress) progress.hidden = true;
    const qCount = $1358149ea3f19aaa$var$$(".aq-hero-q-count");
    if (qCount) qCount.textContent = `${(0, $42a804d610927c2a$export$aa473411bddb7e17).length} questions`;
    if ($1358149ea3f19aaa$var$DEV_MODE) {
        $1358149ea3f19aaa$var$$$("[data-screen]").forEach((el)=>{
            el.hidden = false;
        });
        $1358149ea3f19aaa$var$renderAllQuestions();
        return;
    }
    $1358149ea3f19aaa$var$showScreen("hero");
}
// --- Screen transitions ---
function $1358149ea3f19aaa$var$showScreen(name) {
    if ($1358149ea3f19aaa$var$DEV_MODE) return;
    $1358149ea3f19aaa$var$state.currentScreen = name;
    $1358149ea3f19aaa$var$$$("[data-screen]").forEach((el)=>{
        el.hidden = el.dataset.screen !== name;
    });
}
// --- Progress bar ---
function $1358149ea3f19aaa$var$updateProgress() {
    const bar = $1358149ea3f19aaa$var$$(".aq-progress-bar");
    if (!bar) return;
    const done = $1358149ea3f19aaa$var$state.currentScreen !== "quiz" ? $1358149ea3f19aaa$var$GROUPED_QUESTIONS.length : $1358149ea3f19aaa$var$state.currentQuestion;
    const pct = done / $1358149ea3f19aaa$var$GROUPED_QUESTIONS.length * 100;
    bar.style.width = pct + "%";
    const counter = $1358149ea3f19aaa$var$$(".aq-progress-count");
    if (counter) {
        if ($1358149ea3f19aaa$var$state.currentScreen !== "quiz") counter.textContent = "";
        else {
            const meta = $1358149ea3f19aaa$var$Q_META[$1358149ea3f19aaa$var$state.currentQuestion];
            counter.textContent = meta ? `${meta.category} \xb7 ${meta.pos} of ${meta.total}` : "";
        }
    }
}
// --- Scroll helper ---
function $1358149ea3f19aaa$var$scrollToQuizTop() {
    const progress = $1358149ea3f19aaa$var$$(".aq-progress");
    if (progress) progress.scrollIntoView({
        behavior: "smooth",
        block: "start"
    });
}
// --- Dev mode: all questions ---
function $1358149ea3f19aaa$var$renderAllQuestions() {
    const container = $1358149ea3f19aaa$var$$("[data-screen='quiz'] .aq-question-area");
    if (!container) return;
    container.innerHTML = "";
    $1358149ea3f19aaa$var$GROUPED_QUESTIONS.forEach(({ idx: idx, question: q }, displayIdx)=>{
        const meta = $1358149ea3f19aaa$var$Q_META[displayIdx];
        const section = document.createElement("div");
        section.className = "aq-question";
        section.style.marginBottom = "3rem";
        section.style.paddingBottom = "2rem";
        section.style.borderBottom = "1px solid #e0dff0";
        const bodyCol = document.createElement("div");
        bodyCol.className = "aq-question-body";
        const label = document.createElement("p");
        label.style.cssText = "font-size:0.8rem;font-weight:600;text-transform:uppercase;letter-spacing:0.06em;color:#6D1E6F;margin:0 0 0.5rem";
        label.textContent = meta ? `${meta.category} \xb7 ${meta.pos} of ${meta.total}` : "";
        bodyCol.appendChild(label);
        const text = document.createElement("h2");
        text.className = "aq-question-text";
        text.textContent = q.text;
        bodyCol.appendChild(text);
        if (q.context) {
            const ctx = document.createElement("p");
            ctx.className = "aq-question-context";
            ctx.textContent = q.context;
            bodyCol.appendChild(ctx);
        }
        const optionsWrap = document.createElement("div");
        optionsWrap.className = "aq-options";
        Object.keys(q.answerAdvice).forEach((opt)=>{
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "aq-option";
            btn.textContent = opt;
            btn.style.cursor = "default";
            optionsWrap.appendChild(btn);
        });
        bodyCol.appendChild(optionsWrap);
        const adviceSection = document.createElement("div");
        adviceSection.style.cssText = "margin-top:1.5rem;display:flex;flex-direction:column;gap:1rem";
        Object.entries(q.answerAdvice).forEach(([opt, advice])=>{
            if (!advice) return;
            const card = document.createElement("div");
            card.style.cssText = "background:#f5f4fb;border-radius:8px;padding:1rem 1.25rem;font-size:0.9rem;line-height:1.6;color:#190D62";
            const optLabel = document.createElement("strong");
            optLabel.style.cssText = "display:block;margin-bottom:0.4rem;font-size:0.8rem;text-transform:uppercase;letter-spacing:0.04em;color:#6D1E6F";
            optLabel.textContent = opt;
            card.appendChild(optLabel);
            const body = document.createElement("div");
            body.className = "s-article";
            body.innerHTML = advice;
            card.appendChild(body);
            adviceSection.appendChild(card);
        });
        bodyCol.appendChild(adviceSection);
        section.appendChild(bodyCol);
        if (q.image) {
            const imageEl = document.createElement("div");
            imageEl.className = "aq-question-image";
            imageEl.innerHTML = q.image;
            section.appendChild(imageEl);
        }
        container.appendChild(section);
    });
}
// --- Quiz screen ---
function $1358149ea3f19aaa$var$renderQuizScreen() {
    const container = $1358149ea3f19aaa$var$$("[data-screen='quiz'] .aq-question-area");
    if (!container) return;
    $1358149ea3f19aaa$var$renderQuestion(container);
}
function $1358149ea3f19aaa$var$renderQuestion(container) {
    const { idx: idx, question: q } = $1358149ea3f19aaa$var$GROUPED_QUESTIONS[$1358149ea3f19aaa$var$state.currentQuestion];
    container.innerHTML = "";
    // Ensure question area is visible, feedback area hidden
    container.hidden = false;
    const feedbackArea = $1358149ea3f19aaa$var$$(".aq-feedback-area");
    if (feedbackArea) feedbackArea.hidden = true;
    // Restore progress bar (hidden during feedback and hero screens)
    const progressEl = $1358149ea3f19aaa$var$$(".aq-progress");
    if (progressEl) progressEl.hidden = false;
    // Show back button only from Q2 onwards
    const backBtn = $1358149ea3f19aaa$var$$(".aq-quiz-back");
    if (backBtn) backBtn.hidden = $1358149ea3f19aaa$var$state.currentQuestion === 0;
    const questionEl = document.createElement("div");
    questionEl.className = "aq-question";
    // Left column: text + options
    const bodyCol = document.createElement("div");
    bodyCol.className = "aq-question-body";
    const text = document.createElement("h2");
    text.className = "aq-question-text";
    text.textContent = q.text;
    bodyCol.appendChild(text);
    if (q.context) {
        const contextEl = document.createElement("p");
        contextEl.className = "aq-question-context";
        contextEl.textContent = q.context;
        bodyCol.appendChild(contextEl);
    }
    const optionsWrap = document.createElement("div");
    optionsWrap.className = "aq-options";
    const existingAnswer = $1358149ea3f19aaa$var$state.answers[idx];
    Object.keys(q.answerAdvice).forEach((opt)=>{
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "aq-option";
        if (existingAnswer === opt) btn.classList.add("aq-option--selected");
        btn.textContent = opt;
        btn.addEventListener("click", ()=>$1358149ea3f19aaa$var$selectOption(idx, opt, container));
        optionsWrap.appendChild(btn);
    });
    bodyCol.appendChild(optionsWrap);
    questionEl.appendChild(bodyCol);
    // Right column: illustration
    const img = q.image;
    if (img) {
        const imageEl = document.createElement("div");
        imageEl.className = "aq-question-image";
        imageEl.innerHTML = img;
        questionEl.appendChild(imageEl);
    }
    container.appendChild(questionEl);
    // Next button — in fixed bar so it's always visible
    const nextBtn = document.createElement("button");
    nextBtn.type = "button";
    nextBtn.className = "aq-next-btn aq-btn-primary";
    nextBtn.disabled = existingAnswer === undefined;
    nextBtn.textContent = "Next \u2192";
    nextBtn.addEventListener("click", $1358149ea3f19aaa$var$renderFeedbackScreen);
    const nextBar = document.createElement("div");
    nextBar.className = "aq-btn-bar";
    const nextBarInner = document.createElement("div");
    nextBarInner.className = "aq-btn-bar-inner";
    nextBarInner.appendChild(nextBtn);
    nextBar.appendChild(nextBarInner);
    container.appendChild(nextBar);
    const firstOption = container.querySelector(".aq-option");
    if (firstOption) firstOption.focus();
}
function $1358149ea3f19aaa$var$renderFeedbackScreen() {
    const { idx: idx, question: q } = $1358149ea3f19aaa$var$GROUPED_QUESTIONS[$1358149ea3f19aaa$var$state.currentQuestion];
    const value = $1358149ea3f19aaa$var$state.answers[idx];
    const norm = (0, $4607f403a8f49bdc$export$46569753fba557ac)(q, value);
    // Skip feedback entirely for N/A answers
    if (norm === null) {
        $1358149ea3f19aaa$var$advanceFromQuestion();
        return;
    }
    // Hide progress bar and back button during feedback
    const progressEl = $1358149ea3f19aaa$var$$(".aq-progress");
    if (progressEl) progressEl.hidden = true;
    const backBtn = $1358149ea3f19aaa$var$$(".aq-quiz-back");
    if (backBtn) backBtn.hidden = true;
    const feedbackArea = $1358149ea3f19aaa$var$$(".aq-feedback-area");
    feedbackArea.innerHTML = "";
    const wrap = document.createElement("div");
    wrap.className = "aq-feedback-screen";
    // Card wrapping the text content
    const card = document.createElement("div");
    card.className = "aq-feedback-card";
    const areaLabel = document.createElement("p");
    areaLabel.className = "aq-feedback-area-label";
    areaLabel.textContent = q.shortLabel;
    card.appendChild(areaLabel);
    const youAnswered = document.createElement("p");
    youAnswered.className = "aq-feedback-you-answered";
    youAnswered.textContent = `You answered "${value}"`;
    card.appendChild(youAnswered);
    const prose = document.createElement("p");
    prose.className = "aq-feedback-prose s-article";
    prose.innerHTML = q.answerAdvice && q.answerAdvice[value] || "";
    card.appendChild(prose);
    wrap.appendChild(card);
    feedbackArea.appendChild(wrap);
    // Continue button in fixed bar
    const continueBtn = document.createElement("button");
    continueBtn.type = "button";
    continueBtn.className = "aq-continue-btn aq-btn-secondary";
    continueBtn.textContent = "Continue \u2192";
    continueBtn.addEventListener("click", $1358149ea3f19aaa$var$advanceFromQuestion);
    const contBar = document.createElement("div");
    contBar.className = "aq-btn-bar";
    const contBarInner = document.createElement("div");
    contBarInner.className = "aq-btn-bar-inner";
    contBarInner.appendChild(continueBtn);
    contBar.appendChild(contBarInner);
    feedbackArea.appendChild(contBar);
    $1358149ea3f19aaa$var$$(".aq-question-area").hidden = true;
    feedbackArea.hidden = false;
    $1358149ea3f19aaa$var$scrollToQuizTop();
    continueBtn.focus();
}
function $1358149ea3f19aaa$var$advanceFromQuestion() {
    const container = $1358149ea3f19aaa$var$$(".aq-question-area");
    $1358149ea3f19aaa$var$scrollToQuizTop();
    if ($1358149ea3f19aaa$var$state.currentQuestion < $1358149ea3f19aaa$var$GROUPED_QUESTIONS.length - 1) {
        $1358149ea3f19aaa$var$state.currentQuestion++;
        $1358149ea3f19aaa$var$updateProgress();
        $1358149ea3f19aaa$var$renderQuestion(container);
    } else {
        const { score: score, perQuestion: perQuestion } = (0, $4607f403a8f49bdc$export$e09e94e4e42c547d)($1358149ea3f19aaa$var$state.answers);
        $1358149ea3f19aaa$var$state.score = score;
        $1358149ea3f19aaa$var$state.perQuestion = perQuestion;
        $1358149ea3f19aaa$var$state.topTwo = (0, $4607f403a8f49bdc$export$bdf6804ae02ab389)(perQuestion);
        $1358149ea3f19aaa$var$state.feedback = (0, $4ed1e0f3eb2386ad$export$5752143a981bab69)(perQuestion);
        $1358149ea3f19aaa$var$state.groupScores = (0, $4607f403a8f49bdc$export$b742977fb0973920)(perQuestion);
        if ($1358149ea3f19aaa$var$PREFILLED_UID) {
            $1358149ea3f19aaa$var$state.uid = $1358149ea3f19aaa$var$PREFILLED_UID;
            (0, $1906e9dcde8b5031$export$972dc84f7790a83a)({
                email: "",
                uid: $1358149ea3f19aaa$var$state.uid,
                score: $1358149ea3f19aaa$var$state.score,
                topTwo: $1358149ea3f19aaa$var$state.topTwo,
                source: $1358149ea3f19aaa$var$state.source,
                groupScores: $1358149ea3f19aaa$var$state.groupScores,
                perQuestion: $1358149ea3f19aaa$var$state.perQuestion
            }).catch((err)=>console.error("Quiz submit error:", err));
            $1358149ea3f19aaa$var$renderResults();
            $1358149ea3f19aaa$var$showScreen("results");
        } else $1358149ea3f19aaa$var$showScreen("email");
        $1358149ea3f19aaa$var$updateProgress();
    }
}
function $1358149ea3f19aaa$var$selectOption(questionIdx, value, container) {
    $1358149ea3f19aaa$var$state.answers[questionIdx] = value;
    $1358149ea3f19aaa$var$$$(".aq-option", container).forEach((btn)=>{
        btn.classList.toggle("aq-option--selected", btn.textContent === value);
    });
    const nextBtn = container.querySelector(".aq-next-btn");
    if (nextBtn) {
        nextBtn.disabled = false;
        nextBtn.focus();
    }
}
// --- Email capture screen ---
function $1358149ea3f19aaa$var$handleEmailSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const emailInput = form.querySelector("[name='email']");
    const honeypot = form.querySelector("[name='website_url']");
    if (honeypot && honeypot.value) return;
    $1358149ea3f19aaa$var$state.email = emailInput.value.trim();
    if (!$1358149ea3f19aaa$var$state.email) return;
    if (window.sendMessage) window.sendMessage("Click", "Resource", "Airtable Maturity Assessment");
    (0, $1906e9dcde8b5031$export$972dc84f7790a83a)({
        email: $1358149ea3f19aaa$var$state.email,
        uid: "",
        score: $1358149ea3f19aaa$var$state.score,
        topTwo: $1358149ea3f19aaa$var$state.topTwo,
        source: $1358149ea3f19aaa$var$state.source,
        groupScores: $1358149ea3f19aaa$var$state.groupScores,
        perQuestion: $1358149ea3f19aaa$var$state.perQuestion
    }).catch((err)=>console.error("Quiz submit error:", err));
    $1358149ea3f19aaa$var$renderResults();
    $1358149ea3f19aaa$var$showScreen("results");
}
// --- Results screen ---
function $1358149ea3f19aaa$var$renderResults() {
    const scoreEl = $1358149ea3f19aaa$var$$(".aq-score-number");
    if (scoreEl) scoreEl.textContent = $1358149ea3f19aaa$var$state.score.toFixed(1);
    const maxEl = $1358149ea3f19aaa$var$$(".aq-score-max");
    if (maxEl) maxEl.textContent = "/ 10";
    const band = (0, $4ed1e0f3eb2386ad$export$2934054be1c59f77)($1358149ea3f19aaa$var$state.score);
    const bandEl = $1358149ea3f19aaa$var$$(".aq-score-band");
    if (bandEl) {
        bandEl.textContent = band.band + " maturity";
        bandEl.dataset.band = band.band.toLowerCase();
    }
    const summaryEl = $1358149ea3f19aaa$var$$(".aq-score-summary");
    if (summaryEl) summaryEl.textContent = band.summary;
    const categoryContainer = $1358149ea3f19aaa$var$$(".aq-category-scores");
    if (categoryContainer) {
        categoryContainer.innerHTML = "";
        const sorted = [
            ...$1358149ea3f19aaa$var$state.groupScores
        ].sort((a, b)=>{
            if (a.norm === null && b.norm === null) return 0;
            if (a.norm === null) return 1;
            if (b.norm === null) return -1;
            return b.norm - a.norm;
        });
        sorted.forEach(({ label: label, score: score, norm: norm })=>{
            const status = (0, $4ed1e0f3eb2386ad$export$ac06e2e0fe5e2dd8)(norm);
            const statusSlug = status ? status.label.toLowerCase().replace(/\s+/g, "-") : "na";
            const pct = norm !== null ? Math.round(norm * 100) : 0;
            const card = document.createElement("div");
            card.className = "aq-cat-score";
            card.dataset.status = statusSlug;
            const header = document.createElement("div");
            header.className = "aq-cat-header";
            header.innerHTML = `<span class="aq-cat-label">${label}</span><span class="aq-cat-number">${score !== null ? score + "/10" : "N/A"}</span>`;
            card.appendChild(header);
            const bar = document.createElement("div");
            bar.className = "aq-cat-bar";
            const fill = document.createElement("div");
            fill.className = "aq-cat-bar-fill";
            fill.style.width = pct + "%";
            bar.appendChild(fill);
            card.appendChild(bar);
            categoryContainer.appendChild(card);
        });
    }
    const topTwoContainer = $1358149ea3f19aaa$var$$(".aq-top-two");
    if (topTwoContainer && $1358149ea3f19aaa$var$state.topTwo.length > 0) {
        // Build index → category label lookup
        const idxToCategory = {};
        (0, $4607f403a8f49bdc$export$ded5a3e043e94a40).forEach(({ label: cat, questionIndices: questionIndices })=>{
            questionIndices.forEach((idx)=>{
                idxToCategory[idx] = cat;
            });
        });
        topTwoContainer.innerHTML = `<h3 class="aq-top-two-heading">Your top ${$1358149ea3f19aaa$var$state.topTwo.length} to address</h3>`;
        $1358149ea3f19aaa$var$state.topTwo.forEach((item, i)=>{
            const q = (0, $42a804d610927c2a$export$aa473411bddb7e17)[item.idx];
            const adviceText = q.answerAdvice && q.answerAdvice[item.answer] || "";
            const cat = idxToCategory[item.idx] || "";
            const div = document.createElement("div");
            div.className = "aq-top-two-item";
            div.innerHTML = `<span class="aq-top-two-number">${i + 1}</span>
        <div>
          <strong>${item.shortLabel}</strong>
          ${cat ? `<span class="aq-top-two-cat">${cat}</span>` : ""}
        </div>`;
            topTwoContainer.appendChild(div);
        });
    }
}
// --- Calendly screen ---
function $1358149ea3f19aaa$var$renderCalendly() {
    const container = $1358149ea3f19aaa$var$$(".aq-calendly-embed");
    if (!container) return;
    const params = new URLSearchParams({
        name: $1358149ea3f19aaa$var$state.name,
        email: $1358149ea3f19aaa$var$state.email,
        a1: $1358149ea3f19aaa$var$state.score,
        a2: $1358149ea3f19aaa$var$state.topTwo.map((t)=>t.shortLabel).join(", ")
    });
    container.innerHTML = "";
    const iframe = document.createElement("iframe");
    iframe.src = `https://calendly.com/tom-halloran-mass/45min?${params.toString()}`;
    iframe.className = "aq-calendly-iframe";
    iframe.setAttribute("frameborder", "0");
    container.appendChild(iframe);
}
// --- Confirmation screen ---
// (static content, no JS needed)
// --- Wire up events ---
document.addEventListener("DOMContentLoaded", ()=>{
    $1358149ea3f19aaa$var$init();
    // Hero start button
    const startBtn = $1358149ea3f19aaa$var$$(".aq-hero-start");
    if (startBtn) startBtn.addEventListener("click", ()=>{
        if (window.sendMessage) window.sendMessage("Click", "Resource", "Airtable Maturity Assessment");
        $1358149ea3f19aaa$var$showScreen("quiz");
        $1358149ea3f19aaa$var$renderQuizScreen();
        $1358149ea3f19aaa$var$updateProgress();
        const progress = $1358149ea3f19aaa$var$$(".aq-progress");
        if (progress) progress.hidden = false;
    });
    const emailForm = $1358149ea3f19aaa$var$$(".aq-email-form");
    if (emailForm) emailForm.addEventListener("submit", $1358149ea3f19aaa$var$handleEmailSubmit);
    const resultsCtaBtn = $1358149ea3f19aaa$var$$(".aq-results-cta-button");
    if (resultsCtaBtn) resultsCtaBtn.addEventListener("click", ()=>{
        if (window.sendMessage) window.sendMessage("Click", "Book a call", "Airtable Maturity Assessment");
        if (window.fireConversion) window.fireConversion();
        $1358149ea3f19aaa$var$renderCalendly();
        $1358149ea3f19aaa$var$showScreen("calendly");
    });
    // Back buttons
    $1358149ea3f19aaa$var$$$(".aq-back-btn").forEach((btn)=>{
        btn.addEventListener("click", ()=>{
            if ($1358149ea3f19aaa$var$state.currentScreen === "email") {
                $1358149ea3f19aaa$var$state.currentQuestion = $1358149ea3f19aaa$var$GROUPED_QUESTIONS.length - 1;
                $1358149ea3f19aaa$var$showScreen("quiz");
                $1358149ea3f19aaa$var$renderQuizScreen();
                $1358149ea3f19aaa$var$updateProgress();
            }
        });
    });
    // Quiz back button — behaviour depends on which view is showing
    const quizBack = $1358149ea3f19aaa$var$$(".aq-quiz-back");
    if (quizBack) quizBack.addEventListener("click", ()=>{
        const feedbackArea = $1358149ea3f19aaa$var$$(".aq-feedback-area");
        if (feedbackArea && !feedbackArea.hidden) {
            // On feedback screen → go back to the current question (allow answer change)
            feedbackArea.hidden = true;
            const container = $1358149ea3f19aaa$var$$(".aq-question-area");
            container.hidden = false;
            const progressEl = $1358149ea3f19aaa$var$$(".aq-progress");
            if (progressEl) progressEl.hidden = false;
            if ($1358149ea3f19aaa$var$state.currentQuestion === 0) quizBack.hidden = true;
            $1358149ea3f19aaa$var$scrollToQuizTop();
        } else // On question screen → go to previous question
        if ($1358149ea3f19aaa$var$state.currentQuestion > 0) {
            $1358149ea3f19aaa$var$state.currentQuestion--;
            $1358149ea3f19aaa$var$updateProgress();
            $1358149ea3f19aaa$var$renderQuizScreen();
        }
    });
});


//# sourceMappingURL=airtable-maturity-assessment.ed4497ea.js.map
